import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import socket from '../../utils/socket';
import ModeEmploi from '../../components/Modal/ModeEmploi';
import './Home.scss';

const Home = () => {
    const navigate = useNavigate();

    // États pour la création
    const [creatorName, setCreatorName] = useState("");
    const [tours, setTours] = useState(10);

    // États pour rejoindre
    const [joinCode, setJoinCode] = useState("");
    const [joinName, setJoinName] = useState("");

    const joinCodeRef = useRef(null);
    const joinNameRef = useRef(null);

    // Liste des parties en cours
    const [publicRooms, setPublicRooms] = useState([]);

    // Message d'erreur
    const [errorMsg, setErrorMsg] = useState("");

    // Modale Mode d'emploi (ouverte automatiquement si pas masquée par l'utilisateur)
    const [showModeEmploi, setShowModeEmploi] = useState(() => {
        return localStorage.getItem('memozyn_hide_mode_emploi') !== 'true';
    });

    useEffect(() => {
        // Nettoyer les sessions résiduelles si l'utilisateur revient sur la page d'accueil
        try {
            const storages = [sessionStorage, localStorage];
            storages.forEach(store => {
                const keysToRemove = [];
                for (let i = 0; i < store.length; i++) {
                    const key = store.key(i);
                    if (key && (key.startsWith('currentPlayer_') || key.startsWith('gameData_'))) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach(k => {
                    const code = k.replace('currentPlayer_', '').replace('gameData_', '');
                    if (socket && code) {
                        socket.emit('quitter_partie', { codePartie: code });
                    }
                    store.removeItem(k);
                });
            });
        } catch (e) {
            console.warn("Nettoyage session :", e);
        }

        const onPublicRoomsList = (roomsList) => {
            setPublicRooms(roomsList || []);
        };

        const onConnect = () => {
            socket.emit('demande_parties_publiques');
        };

        // Branchement des écouteurs
        socket.on('liste_parties_publiques', onPublicRoomsList);
        socket.on('connect', onConnect);

        // Si on est déjà connecté en arrivant sur la page, on demande la liste
        if (socket.connected) {
            socket.emit('demande_parties_publiques');
        }

        // Nettoyage lors du démontage du composant
        return () => {
            socket.off('liste_parties_publiques', onPublicRoomsList);
            socket.off('connect', onConnect);
        };
    }, []);

    const handleCreerPartie = () => {
        if (!creatorName.trim()) {
            setErrorMsg("Veuillez saisir votre pseudo.");
            return;
        }
        setErrorMsg("");

        if (socket) {
            socket.emit('creer_partie', { creatorName: creatorName.trim(), tours }, (response) => {
                if (response && response.success) {
                    sessionStorage.setItem(`currentPlayer_${response.code}`, JSON.stringify(response.player));
                    localStorage.setItem(`currentPlayer_${response.code}`, JSON.stringify(response.player));
                    sessionStorage.setItem(`gameTours_${response.code}`, tours);
                    localStorage.setItem(`gameTours_${response.code}`, tours);
                    
                    navigate(`/inscription/${response.code}`);
                } else {
                    setErrorMsg("Erreur lors de la création de la partie.");
                }
            });
        }
    };

    const handleSelectPartieEnCours = (code) => {
        setJoinCode(code);
        setErrorMsg("");
        if (joinNameRef.current) {
            joinNameRef.current.focus();
        }
    };

    const handleRejoindrePartie = () => {
        const targetCode = joinCode.trim().toUpperCase();
        if (!targetCode || targetCode.length !== 6) {
            setErrorMsg("Veuillez saisir un code de partie à 6 caractères.");
            if (joinCodeRef.current) joinCodeRef.current.focus();
            return;
        }
        
        const pseudo = joinName.trim();
        if (!pseudo) {
            setErrorMsg("Veuillez saisir votre pseudo.");
            if (joinNameRef.current) joinNameRef.current.focus();
            return;
        }
        setErrorMsg("");

        if (socket) {
            socket.emit('rejoindre_partie_nom', { codePartie: targetCode, name: pseudo }, (response) => {
                if (response && response.success) {
                    sessionStorage.setItem(`currentPlayer_${targetCode}`, JSON.stringify(response.player));
                    localStorage.setItem(`currentPlayer_${targetCode}`, JSON.stringify(response.player));
                    
                    navigate(`/inscription/${targetCode}`);
                } else if (response && response.error) {
                    setErrorMsg(response.error);
                } else {
                    setErrorMsg("Impossible de rejoindre la partie.");
                }
            });
        }
    };

    return (
        <div className="home-page">
            {/* Header */}
            <header className="home-header">
                <h1>Mémozyn Studio</h1>
                <h2>L'expérience du jeu interactif ultime</h2>
                <div className="header-actions">
                    <button className="btn-header-action btn-mode-emploi" onClick={() => setShowModeEmploi(true)}>
                        💡 Mode d'emploi
                    </button>
                    <button className="btn-header-action btn-regles" onClick={() => navigate('/rules')}>
                        📖 Règles du jeu
                    </button>
                </div>
            </header>

            {/* Séparation pleine largeur entre le header et le reste du site */}
            <div className="header-full-separator" />

            {errorMsg && <div className="home-error-banner">{errorMsg}</div>}

            {/* Corps principal : 2 cartes + Liste des parties en cours */}
            <div className="home-main-body">
                <div className="home-connection-container">
                    {/* Carte de création */}
                    <div className="connection-card card-creer">
                        <h3>Créer une partie</h3>
                        <div className="form-group">
                            <input 
                                type="text" 
                                placeholder="Votre pseudo (ex: Alice)" 
                                value={creatorName} 
                                onChange={(e) => setCreatorName(e.target.value)}
                                className="input-premium"
                            />
                        </div>
                        <div className="form-group tours-radio-group">
                            <label className="tours-group-title">Nombre de tours :</label>
                            <div className="tours-radio-list">
                                {[5, 10, 15, 20].map((nbTours) => (
                                    <label 
                                        key={nbTours} 
                                        className={`tour-radio-item ${tours === nbTours ? 'selected' : ''}`}
                                    >
                                        <input 
                                            type="radio" 
                                            name="toursSelect" 
                                            value={nbTours} 
                                            checked={tours === nbTours} 
                                            onChange={() => setTours(nbTours)} 
                                        />
                                        <span className="radio-custom"></span>
                                        <span className="tour-label">{nbTours} Tours</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        <button className="btn-action-premium btn-creer" onClick={handleCreerPartie}>
                            Créer une partie 🚀
                        </button>
                    </div>

                    {/* Séparateur visuel */}
                    <div className="connection-separator">OU</div>

                    {/* Carte pour rejoindre */}
                    <div className="connection-card card-rejoindre">
                        <h3>Rejoindre avec un code</h3>
                        <div className="form-group">
                            <input 
                                ref={joinCodeRef}
                                type="text" 
                                placeholder="Code de la partie (ex: XOEUKR)" 
                                value={joinCode} 
                                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                                maxLength={6}
                                className="input-premium code-input"
                            />
                        </div>
                        <div className="form-group">
                            <input 
                                ref={joinNameRef}
                                type="text" 
                                placeholder="Votre pseudo (ex: Bob)" 
                                value={joinName} 
                                onChange={(e) => setJoinName(e.target.value)}
                                className="input-premium"
                            />
                        </div>
                        <button className="btn-action-premium btn-rejoindre" onClick={handleRejoindrePartie}>
                            Rejoindre la partie 📱
                        </button>
                    </div>
                </div>

                {/* Section Parties en cours sous les 2 sections (margin-top: 75px) */}
                <div className="parties-en-cours-container">
                    {publicRooms.length > 0 ? (
                        <div className="parties-list">
                            {publicRooms.map((room) => (
                                <div key={room.codePartie} className="partie-item">
                                    <div className="partie-info">
                                        <span className="partie-badge-live"></span>
                                        <span className="partie-texte">
                                            Partie en cours : <strong>{room.creatorName}</strong>
                                        </span>
                                        <span className="partie-details">
                                            ({room.codePartie} • {room.playersCount}/4 joueurs • {room.tours} tours)
                                        </span>
                                    </div>
                                    <button 
                                        className="btn-rejoindre-fluo"
                                        onClick={() => handleSelectPartieEnCours(room.codePartie)}
                                    >
                                        S'inscrire
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-parties-msg">
                            <span>Aucune partie en cours pour le moment. Créez-en une ci-dessus !</span>
                        </div>
                    )}
                </div>

                {/* Section Présentation du jeu en 3 piliers */}
                <div className="home-presentation-container">
                    <div className="presentation-card card-interactif">
                        <div className="presentation-header">
                            <span className="presentation-icon">🎮</span>
                            <h3>Totalement Interactif</h3>
                        </div>
                        <p>
                            Plongez dans une ambiance de plateau télévisé où votre salon devient une véritable arène de jeu ! Grâce à la synchronisation en temps réel, chaque participant transforme son smartphone ou sa tablette en manette connectée pour buzzer, répondre et défier ses proches dans une convivialité absolue.
                        </p>
                    </div>

                    <div className="presentation-card card-dynamique">
                        <div className="presentation-header">
                            <span className="presentation-icon">⚡</span>
                            <h3>Rythme Ultra Dynamique</h3>
                        </div>
                        <p>
                            Oubliez les temps morts : chaque coup de dé bouleverse la partie ! Entre cases pièges, duels surprises sur les maisons adverses et accélérations fulgurantes grâce aux cases bonus, la compétition reste palpitante et imprévisible jusqu’au tout dernier lancer pour tenir tous les joueurs en haleine.
                        </p>
                    </div>

                    <div className="presentation-card card-pedagogique">
                        <div className="presentation-header">
                            <span className="presentation-icon">🧠</span>
                            <h3>Pédagogie Captivante</h3>
                        </div>
                        <p>
                            Stimulez votre mémoire et votre culture générale sans même vous en rendre compte ! À travers des centaines de questions variées, illustrées et passionnantes, Mémozyn allie le plaisir du jeu de société à l’apprentissage ludique pour éveiller la curiosité de toutes les générations réunies.
                        </p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="home-bottom-bar">
                <footer className="home-footer-compact">
                    &copy; 2026 Tous droits réservés à Memozyn Studio
                </footer>
            </div>

            {/* Modale Mode d'emploi */}
            <ModeEmploi 
                isOpen={showModeEmploi} 
                onClose={() => setShowModeEmploi(false)} 
            />
        </div>
    );
};

export default Home;